import {isEmpty} from 'lodash'
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import {
  COMMON_OUTER_HEIGHT,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
  MOBILE_MAX_WIDTH,
  ROUTES,
} from '../../../../_gori/constants'
import {
  convertUserTimeZone,
  KTSVG,
  setLoadingActionTable,
  StorageHelpers,
  useDisabled,
} from '../../../../_gori/helpers'
import {TableSkeleton} from '../../../../_gori/helpers/components'
import {useBatchesProvider} from '../../../../_gori/helpers/components/BatchesProvider'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import UseRoutesPermission from '../../../../_gori/hooks/UseRoutesPermission'
import {PageTitle} from '../../../../_gori/layout/core'
import {
  Button,
  Table,
  TableBody,
  TableHeader,
  TableTabs,
  TableWrapper,
} from '../../../../_gori/partials/widgets'
import {useAuth} from '../../auth'
import {DragOverlay, useDragOver} from '../../common'
import {BatchesConfig, BatchesStatus} from '../core/_const'
import BatchService from '../core/_requests'
import {BatchesEmptyState} from './BatchesEmptyState'
import {BatchesFilter} from './BatchesFilter'
import {BatchesUpload} from './BatchesUpload'
import {BatchesWalkthrough} from './BatchesWalkthrough'
import useWindowDimensions from '../../../../_gori/hooks/useWindowDimensions'
import clsx from 'clsx'

const WALKTHROUGH_STORAGE_KEY = 'batches_walkthrough_dismissed'

const BatchesPage: React.FC = () => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const {setBatch} = useBatchesProvider()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {setPageDisabled} = useDisabled()
  const [loadingFirst, setLoadingFirst] = useState<boolean>(true)
  const [statistic, setStatistic] = useState<any>([])
  const [tableData, setTableData] = useState<any>([])
  const [pagination, setPagination] = useState<any>({})
  const {loadingSwitch, currentUser} = useAuth()
  const {routes} = UseRoutesPermission()
  const {width} = useWindowDimensions()

  const {isDragOver, handleDrop} = useDragOver()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [showUploadModal, setShowUploadModal] = useState(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)

  const [showWalkthrough, setShowWalkthrough] = useState(false)

  useEffect(() => {
    if (!loadingFirst && !isEmpty(tableData)) {
      const dismissed = StorageHelpers.getItemLocalStorage(WALKTHROUGH_STORAGE_KEY)
      if (!dismissed) {
        setShowWalkthrough(true)
      }
    }
  }, [loadingFirst, tableData])

  const handleDismissWalkthrough = useCallback(() => {
    StorageHelpers.setItemLocalStorage(WALKTHROUGH_STORAGE_KEY, true)
    setShowWalkthrough(false)
  }, [])

  const setTable = useCallback(async () => {
    setPageDisabled(true)
    const config = {
      params: {
        page: searchParams.get('page') || DEFAULT_PAGE,
        page_size: searchParams.get('page_size') || DEFAULT_PAGE_SIZE,
        search_text: searchParams.get('search_text') || null,
        sort_by: searchParams.get('sort_by') || null,
        sort_direction: searchParams.get('sort_direction') || null,
        status: searchParams.get('status') || null,
      },
      cancelToken: newCancelToken(),
    }
    try {
      const response = await BatchService.search(config)
      setTableData(response.batches)
      setPagination(response.pagination)
      setStatistic(response.statistics)
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setPageDisabled(false)
      setLoadingFirst(false)
    }
  }, [isCancel, newCancelToken, searchParams, setPageDisabled])

  const handleFileSelect = useCallback(
    (file: File) => {
      if (!BatchesConfig.SUPPORTED_FORMATS.includes(file.type)) {
        toast.error(intl.formatMessage({id: 'INVALID_FILE_TYPE_CSV_ONLY'}))
        return
      }
      setUploadFile(file)
      setShowUploadModal(true)
    },
    [intl]
  )

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFileSelect(file)
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [handleFileSelect]
  )

  const handleDragDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      const file = handleDrop(e)
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleDrop, handleFileSelect]
  )

  const handleUploadSuccess = useCallback(() => {
    setShowUploadModal(false)
    setUploadFile(null)
    setTable()
  }, [setTable])

  const handleUploadClose = useCallback(() => {
    setShowUploadModal(false)
    setUploadFile(null)
  }, [])

  const handleFileDetail = useCallback(
    (row: number, id: number) => {
      if (!tableData) return
      navigate(`${ROUTES.BATCHES.BASE}/${id}?status=${BatchesStatus.OPEN}`)
      setBatch(tableData[row])
    },
    [tableData, navigate, setBatch]
  )

  const handleDownloadFile = useCallback(
    async (batch: any) => {
      try {
        setTableData(setLoadingActionTable(tableData, [batch.id], true))
        const response = await BatchService.download(batch.id, {
          cancelToken: newCancelToken(),
        })
        if (response) {
          const blob = new Blob([response.content], {type: response.type})
          let link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = response.file_name
          link.click()
        }
      } catch (error) {
        if (isCancel(error)) return
      } finally {
        setTableData(setLoadingActionTable(tableData, [batch.id], false))
      }
    },
    [isCancel, newCancelToken, tableData]
  )

  const columns = useMemo(
    () => [
      {
        id: 'bullet-header',
        Header: intl.formatMessage({id: 'DOWNLOAD'}),
        headerClassName: 'w-125px text-center',
        Cell: ({row}: {row: any}) => (
          <div className='d-flex justify-content-center flex-shrink-0'>
            <div
              title={intl.formatMessage({id: 'FILE_DETAIL'})}
              role={'link'}
              className='btn btn-icon btn-active-color-primary btn-sm me-1 cursor-pointer'
              onClick={() => handleDownloadFile(row.original)}
            >
              {row.original.isLoading ? (
                <div className='spinner-border spinner-border-sm text-primary' role='status' />
              ) : (
                <KTSVG path='/media/gori/batches/file_download_2.svg' className='svg-icon-1' />
              )}
            </div>
          </div>
        ),
      },
      {
        id: 'name',
        Header: intl.formatMessage({id: 'NAME'}),
        accessor: 'name',
        headerClassName: 'min-w-150px',
        cellClassName: 'fw-bolder fs-6 border-0',
        Cell: ({row}: {row: any}) => (
          <div
            role={'link'}
            onClick={() => handleFileDetail(row.index, row.original.id)}
            className='text-hover-primary text-dark cursor-pointer'
          >
            {row.original.name}
          </div>
        ),
      },
      {
        id: 'created_at',
        Header: intl.formatMessage({id: 'UPLOAD_DATE'}),
        accessor: 'created_at',
        headerClassName: 'min-w-100px',
        Cell: ({row}: {row: any}) =>
          row.original.created_at && (
            <>
              <span className='fw-bolder me-1'>
                {convertUserTimeZone(
                  row.original.created_at,
                  currentUser,
                  process.env.REACT_APP_DATE_FORMAT
                )}
              </span>{' '}
              <span className='text-gray-600'>
                {convertUserTimeZone(
                  row.original.created_at,
                  currentUser,
                  process.env.REACT_APP_TIME_FORMAT
                )}
              </span>
            </>
          ),
      },
      {
        id: 'actions-header',
        Header: intl.formatMessage({id: 'DETAIL'}),
        headerClassName: 'w-150px text-center fixed-column',
        cellClassName: 'fixed-column',
        Cell: ({row}: {row: any}) => (
          <div className='d-flex justify-content-center flex-shrink-0'>
            <div
              title={intl.formatMessage({id: 'FILE_DETAIL'})}
              role={'link'}
              onClick={() => handleFileDetail(row.index, row.original.id)}
              className='btn btn-advanced-secondary btn-sm fs-6 rounded-2 w-100px cursor-pointer'
            >
              <span className='me-3'>{intl.formatMessage({id: 'VIEW'})}</span>
              <KTSVG path='/media/gori/arrows/arrow_right.svg' className='svg-icon-3 m-0' />
            </div>
          </div>
        ),
      },
    ],
    [currentUser, handleDownloadFile, handleFileDetail, intl]
  )

  useEffect(() => {
    if (loadingSwitch) return

    setTable()
  }, [loadingSwitch, setTable])

  const isTableEmpty = isEmpty(tableData)

  const uploadFileBtn = useMemo(() => {
    if (!routes.BATCHES_CREATE.hasPermission) return undefined
    return (
      <Button
        event={handleUploadClick}
        className={clsx('btn btn-advanced-primary mb-3', {'w-100': width <= MOBILE_MAX_WIDTH})}
        label={intl.formatMessage({id: 'UPLOAD_CSV'})}
        icon={<KTSVG path='/media/gori/orders/upload_csv.svg' className='me-2 svg-icon-2' />}
      />
    )
  }, [routes.BATCHES_CREATE.hasPermission, handleUploadClick, width, intl])

  return (
    <>
      <input
        ref={fileInputRef}
        type='file'
        accept='.csv'
        onChange={handleFileInputChange}
        style={{display: 'none'}}
      />

      {showUploadModal && uploadFile && (
        <BatchesUpload
          reloadTable={handleUploadSuccess}
          initialFile={uploadFile}
          onClose={handleUploadClose}
        />
      )}

      <BatchesWalkthrough isVisible={showWalkthrough} onDismiss={handleDismissWalkthrough} />

      <PageTitle>{intl.formatMessage({id: 'BATCHES'})}</PageTitle>
      <CSSTransition
        appear
        in
        timeout={300}
        classNames='fade'
        unmountOnExit
        className='d-flex flex-column flex-grow-1 min-h-0 w-100'
      >
        {loadingFirst ? (
          <TableWrapper className='h-100 flex-grow-1 d-flex flex-column'>
            <div className='table-content--inset-inner h-100 flex-grow-1 min-h-0 d-flex flex-column'>
              <TableBody className='position-relative flex-grow-1 min-h-0 h-100'>
                <div className='my-5'>
                  <div className='w-100 h-150px mb-5 placeholder placeholder-lg rounded-2 bg-secondary ' />
                  <div className='mb-5 d-flex flex-fill justify-content-between'>
                    <div className='col-3 placeholder-wave'>
                      <span className='btn col-9 placeholder placeholder-lg rounded-2 bg-secondary' />
                    </div>
                    <div className='col-3 placeholder-wave d-flex flex-row-reverse'>
                      <span className='btn col-9 placeholder placeholder-lg rounded-2 bg-secondary ' />
                    </div>
                  </div>
                </div>
                <TableSkeleton countColumn={3} />
              </TableBody>
            </div>
          </TableWrapper>
        ) : (
          <div className='d-flex flex-column flex-grow-1 min-h-0'>
            <TableTabs
              dataTabs={statistic}
              keyCheckActive='status'
              classNameChild='flex-grow-1 min-h-0 d-flex flex-column'
              children={
                <TableWrapper className='rounded-top-left-0-dt h-100 flex-grow-1 d-flex flex-column'>
                  <div className='table-content--inset-inner h-100 flex-grow-1 min-h-0 d-flex flex-column'>
                    <TableHeader className='min-h-1px my-5 mb-8 d-flex flex-column flex-shrink-0'>
                      <BatchesFilter />
                    </TableHeader>
                    <TableBody
                      height={`calc(100vh - ${COMMON_OUTER_HEIGHT}px)`}
                      className='position-relative flex-grow-1 min-h-0'
                    >
                      {routes.BATCHES_CREATE.hasPermission && !showUploadModal && (
                        <DragOverlay isVisible={isDragOver} onDrop={handleDragDrop} />
                      )}
                      {isTableEmpty ? (
                        routes.BATCHES_CREATE.hasPermission ? (
                          <BatchesEmptyState onFileSelect={handleFileSelect} />
                        ) : (
                          <div className='text-center py-20 text-gray-600 fs-5'>
                            {intl.formatMessage({id: 'NO_DATA'})}
                          </div>
                        )
                      ) : (
                        <Table
                          columns={columns}
                          data={tableData}
                          pagination={pagination}
                          usePagination={showUploadModal || !isDragOver}
                        />
                      )}
                    </TableBody>
                  </div>
                </TableWrapper>
              }
              childrenTab={uploadFileBtn}
            />
          </div>
        )}
      </CSSTransition>
    </>
  )
}

export {BatchesPage}
